import React, { useMemo } from 'react'
import { Box, Paper, styled, useTheme } from '@mui/material'
import {
  GridOnOutlined,
  TextFieldsOutlined,
  FunctionsOutlined,
  WidgetsOutlined,
  AssignmentOutlined,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { SectionSubtitle, SectionTitle } from './SchemaSelectionSection'
import { ComponentBlock } from './blocks/ComponentBlock'
import { ReportComponentType } from 'src/types/report-designer'

export interface IComponentLibrarySectionProps {
  hasTopBar?: boolean
}

export const ComponentLibrarySection: React.FC<IComponentLibrarySectionProps> = ({ hasTopBar = false }) => {
  const theme = useTheme()
  const { t: translate } = useTranslation(['dynamic-report'])

  const BLOCK_ACCENT_COLOR = useMemo(
    () => ({
      [ReportComponentType.TopBar]: theme.palette.primaryDark.main,
      [ReportComponentType.DataTable]: theme.palette.info.main,
      [ReportComponentType.Text]: theme.palette.infoMain.main,
      [ReportComponentType.Function]: theme.palette.warningMain.main,
      [ReportComponentType.Instruction]: theme.palette.success.main,
    }),
    [theme.palette],
  )

  return (
    <LibraryContainer>
      <LibraryHeader>
        <SectionTitle>{translate('component-library', { ns: 'dynamic-report' })}</SectionTitle>
        <SectionSubtitle>{translate('component-library-subtitle', { ns: 'dynamic-report' })}</SectionSubtitle>
      </LibraryHeader>
      <LibraryContent>
        <ComponentBlock
          type={ReportComponentType.TopBar}
          title={translate('block-topBar', { ns: 'dynamic-report' })}
          description={translate('block-topBar-desc', { ns: 'dynamic-report' })}
          icon={<WidgetsOutlined />}
          accentColor={BLOCK_ACCENT_COLOR[ReportComponentType.TopBar]}
          disabled={hasTopBar}
          disabledTooltip={translate('top-bar-only-one-allowed', { ns: 'dynamic-report' })}
        />
        <ComponentBlock
          type={ReportComponentType.DataTable}
          title={translate('block-data-table', { ns: 'dynamic-report' })}
          description={translate('block-data-table-desc', { ns: 'dynamic-report' })}
          icon={<GridOnOutlined />}
          accentColor={BLOCK_ACCENT_COLOR[ReportComponentType.DataTable]}
        />
        <ComponentBlock
          type={ReportComponentType.Text}
          title={translate('block-text', { ns: 'dynamic-report' })}
          description={translate('block-text-desc', { ns: 'dynamic-report' })}
          icon={<TextFieldsOutlined />}
          accentColor={BLOCK_ACCENT_COLOR[ReportComponentType.Text]}
        />
        <ComponentBlock
          type={ReportComponentType.Function}
          title={translate('block-function', { ns: 'dynamic-report' })}
          description={translate('block-function-desc', { ns: 'dynamic-report' })}
          icon={<FunctionsOutlined />}
          accentColor={BLOCK_ACCENT_COLOR[ReportComponentType.Function]}
        />
        <ComponentBlock
          type={ReportComponentType.Instruction}
          title={translate('block-instruction', { ns: 'dynamic-report' })}
          description={translate('block-instruction-desc', { ns: 'dynamic-report' })}
          icon={<AssignmentOutlined />}
          accentColor={BLOCK_ACCENT_COLOR[ReportComponentType.Instruction]}
        />
      </LibraryContent>
    </LibraryContainer>
  )
}

const LibraryContainer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none',
  overflow: 'hidden',
}))

const LibraryHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
}))

const LibraryContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1.5),
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  minHeight: 0,
}))
